/**
 * Offline Queue Service
 *
 * Manages a queue of failed API requests using IndexedDB for offline persistence.
 * Requests are stored when the network is unavailable and automatically synced
 * when connection is restored.
 */

import type { AxiosRequestConfig } from 'axios';
import api from './api';

/**
 * Database Configuration
 */
const DB_NAME = 'offline-queue';
const DB_VERSION = 1;
const STORE_NAME = 'requests';

/**
 * Offline Request Interface
 *
 * Represents a queued API request with metadata for retry logic.
 */
export interface OfflineRequest {
  id?: number;
  config: AxiosRequestConfig;
  timestamp: number;
  retryCount: number;
}

/**
 * Initialize IndexedDB Database
 *
 * Creates the offline-queue database with a 'requests' object store.
 * Uses auto-incrementing 'id' as the primary key.
 *
 * @returns Promise<IDBDatabase> Database instance
 */
function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      reject(new Error(`Failed to open IndexedDB: ${request.error?.message}`));
    };

    request.onsuccess = () => {
      resolve(request.result);
    };

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;

      // Create object store if it doesn't exist
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const objectStore = db.createObjectStore(STORE_NAME, {
          keyPath: 'id',
          autoIncrement: true,
        });

        // Create index on timestamp for sorting
        objectStore.createIndex('timestamp', 'timestamp', { unique: false });
      }
    };
  });
}

/**
 * Add Request to Offline Queue
 *
 * Stores a failed API request in IndexedDB for later retry.
 * Used when network is unavailable or request fails with network error.
 *
 * @param config - Axios request configuration
 * @returns Promise<number> ID of the queued request
 *
 * @example
 * ```typescript
 * try {
 *   await api.post('/recipes', recipeData);
 * } catch (error) {
 *   if (!navigator.onLine) {
 *     await addToQueue({ method: 'POST', url: '/recipes', data: recipeData });
 *   }
 * }
 * ```
 */
export async function addToQueue(config: AxiosRequestConfig): Promise<number> {
  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);

    const request: OfflineRequest = {
      config,
      timestamp: Date.now(),
      retryCount: 0,
    };

    const addRequest = store.add(request);

    addRequest.onsuccess = () => {
      db.close();
      resolve(addRequest.result as number);
    };

    addRequest.onerror = () => {
      db.close();
      reject(new Error(`Failed to add request to queue: ${addRequest.error?.message}`));
    };
  });
}

/**
 * Get All Queued Requests
 *
 * Retrieves all pending requests from IndexedDB, sorted by timestamp (oldest first).
 *
 * @returns Promise<OfflineRequest[]> Array of queued requests
 */
export async function getAllRequests(): Promise<OfflineRequest[]> {
  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const getAllRequest = store.getAll();

    getAllRequest.onsuccess = () => {
      db.close();
      // Sort by timestamp (oldest first)
      const requests = (getAllRequest.result as OfflineRequest[]).sort(
        (a, b) => a.timestamp - b.timestamp
      );
      resolve(requests);
    };

    getAllRequest.onerror = () => {
      db.close();
      reject(new Error(`Failed to get requests from queue: ${getAllRequest.error?.message}`));
    };
  });
}

/**
 * Delete Request from Queue
 *
 * Removes a successfully synced request from IndexedDB.
 *
 * @param id - Request ID to delete
 * @returns Promise<void>
 */
export async function deleteRequest(id: number): Promise<void> {
  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const deleteRequest = store.delete(id);

    deleteRequest.onsuccess = () => {
      db.close();
      resolve();
    };

    deleteRequest.onerror = () => {
      db.close();
      reject(new Error(`Failed to delete request from queue: ${deleteRequest.error?.message}`));
    };
  });
}

/**
 * Update Request Retry Count
 *
 * Increments the retry count for a request that failed to sync.
 * Used to implement exponential backoff or give up after max retries.
 *
 * @param id - Request ID to update
 * @returns Promise<void>
 */
export async function incrementRetryCount(id: number): Promise<void> {
  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);

    const getRequest = store.get(id);

    getRequest.onsuccess = () => {
      const request = getRequest.result as OfflineRequest;
      if (request) {
        request.retryCount += 1;
        const updateRequest = store.put(request);

        updateRequest.onsuccess = () => {
          db.close();
          resolve();
        };

        updateRequest.onerror = () => {
          db.close();
          reject(new Error(`Failed to update retry count: ${updateRequest.error?.message}`));
        };
      } else {
        db.close();
        reject(new Error(`Request with id ${id} not found`));
      }
    };

    getRequest.onerror = () => {
      db.close();
      reject(new Error(`Failed to get request: ${getRequest.error?.message}`));
    };
  });
}

/**
 * Clear All Requests from Queue
 *
 * Removes all queued requests from IndexedDB.
 * Useful for testing or when user wants to discard offline changes.
 *
 * @returns Promise<void>
 */
export async function clearQueue(): Promise<void> {
  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const clearRequest = store.clear();

    clearRequest.onsuccess = () => {
      db.close();
      resolve();
    };

    clearRequest.onerror = () => {
      db.close();
      reject(new Error(`Failed to clear queue: ${clearRequest.error?.message}`));
    };
  });
}

/**
 * Get Queue Size
 *
 * Returns the number of pending requests in the queue.
 *
 * @returns Promise<number> Number of queued requests
 */
export async function getQueueSize(): Promise<number> {
  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const countRequest = store.count();

    countRequest.onsuccess = () => {
      db.close();
      resolve(countRequest.result);
    };

    countRequest.onerror = () => {
      db.close();
      reject(new Error(`Failed to get queue size: ${countRequest.error?.message}`));
    };
  });
}

/**
 * Sync Offline Queue
 *
 * Attempts to replay all queued requests when network connection is restored.
 * Successfully synced requests are removed from the queue.
 * Failed requests remain in the queue with incremented retry count.
 *
 * @param maxRetries - Maximum number of retry attempts (default: 3)
 * @returns Promise<{ success: number, failed: number }> Sync results
 *
 * @example
 * ```typescript
 * window.addEventListener('online', async () => {
 *   const result = await syncQueue();
 *   console.log(`Synced ${result.success} requests, ${result.failed} failed`);
 * });
 * ```
 */
export async function syncQueue(maxRetries: number = 3): Promise<{ success: number; failed: number }> {
  if (!navigator.onLine) {
    return { success: 0, failed: 0 };
  }

  const requests = await getAllRequests();
  let successCount = 0;
  let failedCount = 0;

  for (const request of requests) {
    // Skip requests that have exceeded max retries
    if (request.retryCount >= maxRetries) {
      failedCount++;
      continue;
    }

    try {
      // Attempt to replay the request
      await api.request(request.config);

      // Success - remove from queue
      if (request.id !== undefined) {
        await deleteRequest(request.id);
      }
      successCount++;
    } catch (error) {
      // Failed - increment retry count
      if (request.id !== undefined) {
        try {
          await incrementRetryCount(request.id);
        } catch (updateError) {
          // Ignore update errors
        }
      }
      failedCount++;
    }
  }

  return { success: successCount, failed: failedCount };
}

/**
 * Check if IndexedDB is supported
 *
 * @returns boolean
 */
export function isIndexedDBSupported(): boolean {
  return typeof indexedDB !== 'undefined';
}

/**
 * Initialize Offline Queue on Module Load
 *
 * Pre-opens the database to ensure it's created and ready.
 * This prevents delays on the first queue operation.
 */
if (isIndexedDBSupported()) {
  openDatabase().catch((error) => {
    console.error('Failed to initialize offline queue database:', error);
  });
}

/**
 * Background Sync Setup
 *
 * Automatically syncs the offline queue when network connection is restored.
 * This listener ensures that any queued requests from offline usage are
 * automatically replayed when the user comes back online.
 */
if (typeof window !== 'undefined') {
  window.addEventListener('online', async () => {
    try {
      const result = await syncQueue();

      if (result.success > 0 || result.failed > 0) {
        // Notify user of sync results via custom event
        window.dispatchEvent(
          new CustomEvent('offline-queue-synced', {
            detail: {
              success: result.success,
              failed: result.failed,
            },
          })
        );
      }
    } catch (error) {
      console.error('Failed to sync offline queue:', error);
    }
  });
}
